import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { AlertTriangle, Users, UserCheck, ArrowLeft } from 'lucide-react';

interface SessionPlayer {
  playerId: string;
  name: string;
  nickname: string | null;
  buyIns: number;
  cashOut: string;
}

interface VarianceModalProps {
  open: boolean;
  onClose: () => void;
  variance: number;
  totalBuyIns: number;
  totalCashOuts: number;
  sessionPlayers: SessionPlayer[];
  onSplitEqually: (adjustedPlayers: SessionPlayer[]) => void;
  onAssignToPlayer: (adjustedPlayers: SessionPlayer[]) => void;
  onReenter: () => void;
}

export default function VarianceModal({
  open,
  onClose,
  variance,
  totalBuyIns,
  totalCashOuts,
  sessionPlayers,
  onSplitEqually,
  onAssignToPlayer,
  onReenter,
}: VarianceModalProps) {
  const [mode, setMode] = useState<'choose' | 'split-preview' | 'assign'>('choose');
  const [manualAdjustments, setManualAdjustments] = useState<Record<string, string>>({});

  const perPlayer = variance / sessionPlayers.length;
  const remainingVariance =
    variance -
    Object.values(manualAdjustments).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);

  const handleSplitConfirm = () => {
    const adjusted = sessionPlayers.map((sp) => ({
      ...sp,
      cashOut: String((parseFloat(sp.cashOut) || 0) + perPlayer),
    }));
    onSplitEqually(adjusted);
  };

  const handleAssignConfirm = () => {
    const adjusted = sessionPlayers.map((sp) => ({
      ...sp,
      cashOut: String((parseFloat(sp.cashOut) || 0) + (parseFloat(manualAdjustments[sp.playerId] || '0') || 0)),
    }));
    onAssignToPlayer(adjusted);
  };

  const resetMode = () => {
    setMode('choose');
    setManualAdjustments({});
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { resetMode(); onClose(); } }}>
      <DialogContent className="max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Results Mismatch
          </DialogTitle>
          <DialogDescription>
            Total entries: <span className="font-mono font-bold">{totalBuyIns}</span> — Total results:{' '}
            <span className="font-mono font-bold">{totalCashOuts}</span>
            <br />
            Variance: <span className="font-mono font-bold text-destructive">{variance.toFixed(2)}</span>
          </DialogDescription>
        </DialogHeader>

        {mode === 'choose' && (
          <div className="space-y-2 mt-2">
            <Button variant="outline" className="w-full justify-start gap-2 h-12" onClick={() => setMode('split-preview')}>
              <Users className="h-4 w-4" />
              Split equally ({perPlayer.toFixed(2)}/player)
            </Button>
            <Button variant="outline" className="w-full justify-start gap-2 h-12" onClick={() => setMode('assign')}>
              <UserCheck className="h-4 w-4" />
              Assign manually
            </Button>
            <Button variant="outline" className="w-full justify-start gap-2 h-12" onClick={() => { resetMode(); onReenter(); }}>
              <ArrowLeft className="h-4 w-4" />
              Re-enter results
            </Button>
          </div>
        )}

        {mode === 'split-preview' && (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">
              Each player's result will be adjusted by <span className="font-mono font-bold">{perPlayer.toFixed(2)}</span>
            </p>
            <ScrollArea className="max-h-[40vh] pr-4">
              <div className="space-y-1">
                {sessionPlayers.map((sp) => {
                  const original = parseFloat(sp.cashOut) || 0;
                  const adjusted = original + perPlayer;
                  return (
                    <div key={sp.playerId} className="flex justify-between text-sm py-1 border-b border-border/50">
                      <span className="font-medium">{sp.name}</span>
                      <span className="font-mono">
                        {original} → <span className="font-bold">{adjusted.toFixed(2)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={resetMode} className="flex-1">Back</Button>
              <Button onClick={handleSplitConfirm} className="flex-1">Confirm</Button>
            </div>
          </div>
        )}

        {mode === 'assign' && (
          <div className="space-y-3 mt-2">
            <div className={cn(
              'text-center p-2 rounded-lg text-sm font-mono font-bold',
              Math.abs(remainingVariance) < 0.01 ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
            )}>
              Remaining: {remainingVariance.toFixed(2)}
            </div>
            <ScrollArea className="max-h-[40vh] pr-4">
              <div className="space-y-2">
                {sessionPlayers.map((sp) => (
                  <div key={sp.playerId} className="flex items-center gap-2">
                    <span className="text-sm font-medium flex-1">{sp.name}</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder="0"
                      value={manualAdjustments[sp.playerId] || ''}
                      onChange={(e) =>
                        setManualAdjustments((prev) => ({ ...prev, [sp.playerId]: e.target.value }))
                      }
                      className="w-24 h-9 font-mono text-sm"
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={resetMode} className="flex-1">Back</Button>
              <Button
                onClick={handleAssignConfirm}
                disabled={Math.abs(remainingVariance) >= 0.01}
                className="flex-1"
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
